<template>
<form @submit.prevent="handleSubmit()">
  <b-loading :active="loading" :is-full-page="false" />

  <template v-if="!loading">
    <cytomine-modal :active="active" :title="$t('snapshot')" @close="$emit('update:active', false)">

      <template>
        <div class="snapshot-grid">
          <div><b>{{$t('start-x')}}</b></div>
          <div><b>{{$t('start-y')}}</b></div>
          <div><b>{{$t('width')}}</b></div>
          <div><b>{{$t('height')}}</b></div>

          <div>{{ snapshotArea.left }}</div>
          <div>{{ snapshotArea.bottom }}</div>
          <div>{{ snapshotArea.w }}</div>
          <div>{{ snapshotArea.h }}</div>
        </div>
      </template>
      <hr>

      <div class="snapshot-option-grid">
        <!-- Export 형식 -->
        <div class="option-label">Export Format</div>
        <div class="option-value">
          <b-radio v-model="exportFormat" native-value="jpg">jpg</b-radio>
          <b-radio v-model="exportFormat" native-value="png">png</b-radio>
          <b-radio v-model="exportFormat" native-value="webp">webp</b-radio>
        </div>

        <!-- Annotation 포함 여부 -->
        <div class="option-label">{{$t('include-annotations')}}</div>
        <div class="option-value">
          <b-radio v-model="annotationOption" native-value="none">{{$t('none')}}</b-radio>
          <b-radio v-model="annotationOption" native-value="selected" :disabled="!selectedAnnotationIds">{{$t('selected-only')}}</b-radio>
          <b-radio v-model="annotationOption" native-value="all">{{$t('all')}}</b-radio>
        </div>

        <!-- Border Color -->
        <div class="option-label">{{$t('border-color')}}</div>
        <div class="option-value">
          <b-radio
            v-for="color in Object.keys(borderColorMap)"
            :key="color"
            v-model="borderColor"
            :native-value="color"
            :disabled="annotationOption === 'none'"
          >
            {{ $t(color) }}
          </b-radio>
        </div>

        <!-- Border Thickness -->
        <div class="option-label">{{$t('border-thickness')}}</div>
        <div class="option-value">
          <b-input 
            v-model="borderThickness" 
            type="number" 
            min="1"
            max="10"
            @blur="validateBorderThickness"
            :disabled="annotationOption === 'none'"
          ></b-input>
        </div>
        
        <!-- Blind 모드 -->
        <div class="option-label">{{$t('blind-mode')}}</div>
        <div class="option-value">
          <b-checkbox v-model="blindMode">{{$t('enable')}}</b-checkbox>
        </div>
      </div>

      <!-- footer buttons -->
      <template #footer>
        <button class="button" type="button" @click="$emit('update:active', false)"> {{$t('button-cancel')}} </button>
        <button class="button is-link" type="submit" :disabled="loading"> {{$t('button-confirm')}} </button>
      </template>

    </cytomine-modal>
  </template>
</form>
</template>

<script>
import CytomineModal from '@/components/utils/CytomineModal';
import {get} from '@/utils/store-helpers';
import {appendShortTermToken} from '@/utils/token-utils.js';

export default {
  name: 'snapshot-modal',
  props: {
    active: Boolean,
    imageId: Number,
    imageName: String,
    snapshotArea: {
      type: Object,
      default: () => ({
        left: 0, 
        right: 0,
        top: 0,
        bottom: 0,
        x: 0,
        y: 0,
        w: 0,
        h: 0
      }),
    },
    totalAnnotationIds: String,
    selectedAnnotationIds: String,
  },
  components: {CytomineModal},
  data() {
    return {
      loading: false,

      exportFormat: 'jpg',
      annotationOption: 'none',

      borderColor: 'Black',
      borderColorMap: {
        Black:  '0x000000',
        White:  '0xFFFFFF',
        Red:    '0xFF0000',
        Blue:   '0x0000FF',
        Yellow: '0xFFFF00',
        Green:  '0x00FF00',
      },

      borderThickness: 1,
      blindMode: false,

      includeAnnotationIds: '',
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    currentUser: get('currentUser/user'),
  },
  watch: {
    selectedAnnotationIds(val) {
      if (!val && this.annotationOption === 'selected') {
        this.annotationOption = 'none';
      }
    },
  },
  methods: {
    validateBorderThickness() {
      let v = Number(this.borderThickness);

      if (isNaN(v) || v <= 0) {
        this.borderThickness = 1;
      }
      else if (v > 10) {
        this.borderThickness = 10;
      }
    },
    getAnnotationIdsParam() {
      if (this.annotationOption == 'none') {
        return '';
      }
      else if (this.annotationOption == 'selected') {
        return 'annotationIds=' + this.selectedAnnotationIds;
      }
      else if (this.annotationOption == 'all') {
        return 'annotationIds=' + this.totalAnnotationIds;
      }
    },
    getBorderColorParam() {
      return 'borderColor=' + this.borderColorMap[this.borderColor] ?? '0x000000';
    },
    getBorderThicknessParam() {
      return 'borderThickness=' + this.borderThickness;
    },
    clearSelection() {
      this.annotationOption = 'none';
    },
    async handleSubmit() {

      let paramList = [
        this.getAnnotationIdsParam(),
        this.getBorderColorParam(),
        this.getBorderThicknessParam(),
      ].filter(p => p && p.trim() !== '');

      let param = paramList.length > 0
        ? '?' + paramList.join('&')
        : '';
        
      console.log(param);

      // URL 생성
      const url = `/api/imageinstance/${this.imageId}/window-${this.snapshotArea.x}-${this.snapshotArea.y}-${this.snapshotArea.w}-${this.snapshotArea.h}.${this.exportFormat}` + param;
      const finalUrl = appendShortTermToken(url, this.shortTermToken);
      console.log(finalUrl);

      this.loading = true;
      try {
        const response = await fetch(finalUrl, {
          method: 'GET',
          headers: {
            'X-Image-Size-Safety': 'SAFE_RESIZE'
          }
        });
        if (!response.ok) {
          throw new Error('스냅샷 다운로드 실패');
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${this.blindMode ? this.imageId : this.imageName}.${this.exportFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(blobUrl);
      }
      catch (error) {
        console.error(error);
      }

      
      this.loading = false;

      this.$emit('update:active', false);
      this.clearSelection();
    },
  },
};
</script>


<style scoped>
.snapshot-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  text-align: center;
}
.snapshot-grid > div {
  padding: 4px 0;
}


.snapshot-option-grid {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 12px 20px;
  align-items: center;
}

.option-label {
  font-weight: 600;
}

.option-value > * {
  margin-right: 12px; /* 라디오 버튼 간격 */
}
</style>
